import { useState } from 'react';

import { Copy, Key, Plus, Trash2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateServiceHistoryToken,
  useRevokeServiceHistoryToken,
  useServiceHistoryTokens,
} from '@/hooks/useServiceHistory';
import { getEnv } from '@/lib/env';

/** API tokens for custom tools that call the service history lookup. */
export const ServiceHistoryTokensCard = () => {
  const { data: tokens, isLoading } = useServiceHistoryTokens();
  const createToken = useCreateServiceHistoryToken();
  const revokeToken = useRevokeServiceHistoryToken();
  const [name, setName] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const lookupUrl = `${getEnv('VITE_API_URL')}/service-history/lookup?registration=AB12CDE`;

  const handleCreate = async () => {
    if (!name.trim()) return;
    const result = await createToken.mutateAsync(name.trim());
    setCreatedToken(result.token);
    setName('');
  };

  const handleCopy = async () => {
    if (!createdToken) return;
    await navigator.clipboard.writeText(createdToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Key className="text-muted-foreground h-5 w-5" />
          <CardTitle className="text-sm">API tokens for custom tools</CardTitle>
        </div>
        <CardDescription>
          A custom tool calls <code className="text-xs">GET {lookupUrl}</code> with the header{' '}
          <code className="text-xs">Authorization: Bearer &lt;token&gt;</code>. It returns the
          current summary and key facts, or <code className="text-xs">found: false</code> when the
          car has no records.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {createdToken && (
          <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950">
            <code className="flex-1 text-xs break-all">{createdToken}</code>
            <Button variant="ghost" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4" />
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCreatedToken(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        {createdToken && (
          <p className="text-muted-foreground text-xs">Copy it now. It will not be shown again.</p>
        )}

        <div className="flex gap-2">
          <Input
            placeholder="Token name (e.g. sales-agent tool)"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleCreate()}
            className="max-w-xs text-sm"
          />
          <Button size="sm" onClick={handleCreate} disabled={!name.trim() || createToken.isPending}>
            <Plus className="mr-1 h-4 w-4" />
            Create
          </Button>
        </div>

        {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
        {!isLoading && !tokens?.length && (
          <p className="text-muted-foreground text-sm">No tokens yet.</p>
        )}
        {!!tokens?.length && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((token) => (
                <TableRow key={token.id}>
                  <TableCell className="text-sm font-medium">{token.name}</TableCell>
                  <TableCell className="font-mono text-xs">{token.token_prefix}…</TableCell>
                  <TableCell>
                    <Badge variant={token.is_active ? 'default' : 'secondary'}>
                      {token.is_active ? 'Active' : 'Revoked'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {token.last_used_at
                      ? new Date(token.last_used_at).toLocaleString('en-GB')
                      : 'Never'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(token.created_at).toLocaleDateString('en-GB')}
                  </TableCell>
                  <TableCell>
                    {token.is_active && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Revoke ${token.name}`}
                        onClick={() => revokeToken.mutate(token.id)}
                        disabled={revokeToken.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
